import React, { useEffect, useState } from "react";
import { Button, Form, Table, Modal, Card, Row, Col, Alert } from "react-bootstrap";
import axios from "axios";
import Layout from "../../components/layouts/Main";
import config from "../../config";
import { toast } from "react-toastify";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";

export default function EditSalesOrder() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [showItemModal, setShowItemModal] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);

    // Permission check
    const canEditSO = useHasPermission(PERMISSIONS.SO_EDIT);

    const handleOpenItemModal = () => {
        setEditingIndex(null);
        setNewItem({
            productId: "",
            quality: "",
            quantity: 0,
            unit: "",
            unitPrice: 0,
            tax: 0,
            taxAmount: 0,
            baseAmount: 0,
            totalPrice: 0,
            grossOrNet: "Gross",
            labTestRequired: false,
            customMarking: "",
            packingSize: "",
            packingBagType: "",
        });
        setShowItemModal(true);
    };
    const handleCloseItemModal = () => {
        setShowItemModal(false);
        setEditingIndex(null);
    };

    const handleNextStep = () => setStep((prev) => prev + 1);

    const [orderData, setOrderData] = useState({
        orderNumber: "",
        buyerId: "",
        buyerBillTo: { name: "", address: "" },
        buyerDeliveryDetails: { name: "", address: "" },
        sameAsBillingAddress: true,
        buyerPONumber: "",
        buyerPOAttachment: "",
        brokerId: null,
        deliveryTerms: "",
        paymentTerm: "",
        transportation: false,
        transportationAmount: 0,
        taxAmount: 0,
        totalAmount: 0,
        remarks: "",
        EDD: "",
        labTestRequired: false,
        orderDate: new Date().toISOString().split("T")[0],
    });

    const [newItem, setNewItem] = useState({
        productId: "",
        quality: "",
        quantity: 0,
        unit: "",
        unitPrice: 0,
        tax: 0,
        taxAmount: 0,
        baseAmount: 0,
        totalPrice: 0,
        grossOrNet: "Gross",
        labTestRequired: false,
        customMarking: "",
        packingSize: "",
        packingBagType: "",
    });

    const [items, setItems] = useState([]);
    const [products, setProducts] = useState([]);
    const [brokers, setBrokers] = useState([]);
    const [buyers, setBuyers] = useState([]);

    useEffect(() => {
        async function fetchData() {
            try {
                const [prodRes, brokerRes, buyerRes, matRes, orderRes, orderItemsRes] = await Promise.all([
                    axios.get(`${config.API_BASE_URL}/products?populateUnit=true`),
                    axios.get(`${config.API_BASE_URL}/brokers?approved=true&status=Active`),
                    axios.get(`${config.API_BASE_URL}/buyers?approved=true&status=Active`),
                    axios.get(`${config.API_BASE_URL}/packing-materials`),
                    axios.get(`${config.API_BASE_URL}/sales-orders/${id}`),
                    axios.get(`${config.API_BASE_URL}/sales-order-items?salesOrderId=${id}`),
                ]);

                setProducts(prodRes.data);
                setBrokers(brokerRes.data);
                setBuyers(buyerRes.data);
                setMaterials(matRes.data);

                // Fill order data
                const order = orderRes.data.order;
                if (order.EDD) {
                    order.EDD = order.EDD.split("T")[0];
                }
                setOrderData(prev => ({
                    ...prev,
                    ...order,
                    buyerId: order.buyer || "",
                    brokerId: order.broker || "",
                    buyerBillTo: order.buyerBillTo || { name: "", address: "" },
                    buyerDeliveryDetails: order.buyerDeliveryDetails || { name: "", address: "" },
                }));

                const OrderedItems = orderItemsRes.data;
                setItems(
                    OrderedItems.map((it) => ({
                        ...it,
                        productId: it.product?._id || "",
                    }))
                );

                setLoading(false);
            } catch (err) {
                console.error("Error fetching data:", err);
                toast.error("Failed to load sales order");
                setLoading(false);
            }
        }
        fetchData();
    }, [id]);

    const handleOrderChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith("buyerBillTo.") || name.startsWith("buyerDeliveryDetails.")) {
            const [parent, key] = name.split(".");
            setOrderData((prev) => ({
                ...prev,
                [parent]: { ...prev[parent], [key]: value },
            }));
        } else {
            setOrderData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleItemChange = (e) => {
        const { name, value } = e.target;
        setNewItem((prev) => {
            let updatedItem = { ...prev, [name]: value };
            if (name === "productId") {
                const selectedProduct = products.find((p) => p._id === value);
                if (selectedProduct) {
                    updatedItem = {
                        ...updatedItem,
                        tax: selectedProduct.gst || 0,
                        unit: selectedProduct.unitId.unitCode || "",
                    };
                }
            }
            if (name === "quantity" || name === "unitPrice") {
                const baseAmount = updatedItem.quantity * updatedItem.unitPrice;
                const taxAmount = (baseAmount * (updatedItem.tax || 0)) / 100;
                updatedItem = { ...updatedItem, baseAmount, taxAmount, totalPrice: baseAmount + taxAmount };
            }
            return updatedItem;
        });
    };

    // for showing unit in modal
    const selectedProduct = products.find((p) => p._id === newItem.productId);

    const openEditItemModal = (idx) => {
        setEditingIndex(idx);
        setNewItem({ ...items[idx] });
        setShowItemModal(true);
    };

    const removeItem = (index) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    const handleUpdate = async () => {
        if (!orderData.buyerId) {
            toast.error("Buyer is required");
            return;
        }

        const itemsWithCalc = items.map((it) => ({
            ...it,
            product: it.productId,
        }));

        const subtotal = itemsWithCalc.reduce((sum, it) => sum + (it.baseAmount || 0), 0);
        const totalTax = itemsWithCalc.reduce((sum, it) => sum + (it.taxAmount || 0), 0);
        const totalAmount = subtotal + totalTax + (Number(orderData.transportationAmount) || 0);

        const payload = {
            ...orderData,
            buyer: orderData.buyerId || undefined,
            broker: orderData.brokerId || undefined,
            taxAmount: totalTax,
            totalAmount,
            items: itemsWithCalc,
        };

        try {
            await axios.put(`${config.API_BASE_URL}/sales-orders/${id}`, payload);
            toast.success("Sales Order updated successfully");
            navigate("/sales-orders");
        } catch (err) {
            console.error("Error updating sales order", err);
            toast.error("Error updating sales order");
        }
    };

    if (loading) return <Layout>Loading...</Layout>;

    // Check permission
    if (!canEditSO) {
        return (
            <Layout>
                <nav aria-label="breadcrumb" className="mb-3">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <Link to="/sales-orders">Sales Orders</Link>
                        </li>
                        <li className="breadcrumb-item active">Access Denied</li>
                    </ol>
                </nav>
                <Alert variant="danger" className="d-flex align-items-center">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <div>
                        <strong>Access Denied</strong>
                        <p className="mb-0">You don't have permission to edit sales orders. Contact your administrator for access.</p>
                    </div>
                </Alert>
            </Layout>
        );
    }

    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to="/sales-orders">Sales Orders</Link>
                    </li>
                    <li className="breadcrumb-item active">Edit</li>
                </ol>
            </nav>

            <h2 className="mb-3">Edit Sales Order</h2>

            {step === 1 && (
                <Form>
                    <div className="row">
                        {/* LEFT COLUMN */}
                        <div className="col-md-6">
                            {/* Buyer */}
                            <Form.Group className="mb-3">
                                <Form.Label>Buyer</Form.Label>
                                <Form.Select name="buyerId" value={orderData.buyerId} onChange={handleOrderChange}>
                                    <option value="">Select...</option>
                                    {buyers.map((b) => (
                                        <option key={b._id} value={b._id}>
                                            {b.business_name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            {/* Broker */}
                            <Form.Group className="mb-3">
                                <Form.Label>Broker</Form.Label>
                                <Form.Select name="brokerId" value={orderData.brokerId} onChange={handleOrderChange}>
                                    <option value="">Select...</option>
                                    {brokers.map((b) => (
                                        <option key={b._id} value={b._id}>
                                            {b.business_name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            {/* Delivery Terms */}
                            <Form.Group className="mb-3">
                                <Form.Label>Delivery Terms</Form.Label>
                                <Form.Select name="deliveryTerms" value={orderData.deliveryTerms} onChange={handleOrderChange}>
                                    <option value="">Select...</option>
                                    <option value="Ex Factory">Ex Factory</option>
                                    <option value="Bhada Paid">Bhada Paid</option>
                                    <option value="FOB">FOB</option>
                                    <option value="CIF">CIF</option>
                                </Form.Select>
                            </Form.Group>

                            {/* Buyer PO Number */}
                            <Form.Group className="mb-3">
                                <Form.Label>Buyer PO Number</Form.Label>
                                <Form.Control name="buyerPONumber" value={orderData.buyerPONumber} onChange={handleOrderChange} />
                            </Form.Group>

                            {/* Payment Term */}
                            <Form.Group className="mb-3">
                                <Form.Label>Payment Term</Form.Label>
                                <Form.Control name="paymentTerm" value={orderData.paymentTerm} onChange={handleOrderChange} />
                            </Form.Group>
                        </div>

                        {/* RIGHT COLUMN */}
                        <div className="col-md-6">
                            {/* Billing Address */}
                            <Form.Group className="mb-3">
                                <Form.Label>Billing Address</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={6}
                                    value={orderData.buyerBillTo.address}
                                    onChange={(e) =>
                                        setOrderData((prev) => ({
                                            ...prev,
                                            buyerBillTo: { ...prev.buyerBillTo, address: e.target.value },
                                        }))
                                    }
                                />
                            </Form.Group>

                            {/* Checkbox: Same as Billing */}
                            <Form.Group className="mb-2">
                                <Form.Check
                                    type="checkbox"
                                    label="Delivery Address same as Billing Address"
                                    checked={orderData.sameAsBillingAddress}
                                    onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        setOrderData((prev) => ({
                                            ...prev,
                                            sameAsBillingAddress: isChecked,
                                            buyerDeliveryDetails: isChecked
                                                ? { ...prev.buyerBillTo }
                                                : { name: "", address: "" },
                                        }));
                                    }}
                                />
                            </Form.Group>

                            {/* Delivery Address */}
                            <Form.Group className="mb-3">
                                <Form.Label>Delivery Address</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={6}
                                    value={orderData.buyerDeliveryDetails.address}
                                    disabled={orderData.sameAsBillingAddress}
                                    onChange={(e) =>
                                        setOrderData((prev) => ({
                                            ...prev,
                                            buyerDeliveryDetails: { ...prev.buyerDeliveryDetails, address: e.target.value },
                                        }))
                                    }
                                />
                            </Form.Group>
                        </div>
                    </div>
                    <div className="row">
                        {/* Remarks */}
                            <Form.Group className="mb-3">
                                <Form.Label>Remarks</Form.Label>
                                <Form.Control as="textarea" name="remarks" value={orderData.remarks} onChange={handleOrderChange} />
                            </Form.Group>
                    </div>

                    {/* NEXT BUTTON */}
                    <div className="row my-3">
                        <div className="col text-end">
                            <Button onClick={handleNextStep}>Next</Button>
                        </div>
                    </div>
                </Form>
            )}

            {step === 2 && (
                <>

                    <Card>
                        <Card.Body className="overflow-auto">
                            <Table striped bordered>
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Quality</th>
                                        <th>Qty</th>
                                        <th>Packing Size</th>
                                        <th>Packing Bag Type</th>
                                        <th>Unit Price (INR)</th>
                                        <th>Tax (%)</th>
                                        <th>Tax Amount (INR)</th>
                                        <th>Total (INR)</th>
                                        <th>Gross/Net</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((it, idx) => (
                                        <tr key={idx}>
                                            <td>{products.find((p) => p._id === it.productId)?.productName}</td>
                                            <td>{it.quality}</td>
                                            <td>{it.quantity}</td>
                                            <td>{it.packingSize}</td>
                                            <td>{materials.find((m) => m._id === it.packingBagType)?.name}</td>
                                            <td>{it.unitPrice}</td>
                                            <td>{it.tax}%</td>
                                            <td>{it.taxAmount ? Number(it.taxAmount).toFixed(2) : '0.00'}</td>
                                            <td>{it.totalPrice ? Number(it.totalPrice).toFixed(2) : '0.00'}</td>
                                            <td>{it.grossOrNet}</td>
                                            <td className="text-nowrap">
                                                <Button 
                                                    variant="soft-primary" 
                                                    size="sm" 
                                                    className="me-2"
                                                    onClick={() => openEditItemModal(idx)}
                                                >
                                                    <i className="fas fa-edit"></i> Edit
                                                </Button>
                                                <Button variant="soft-danger" size="sm" onClick={() => removeItem(idx)}>
                                                    <i className="fas fa-trash"></i> Remove
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                    <div className="p-2">
                        <Button variant="dark" onClick={handleOpenItemModal}>
                            + Add New Item (Product)
                        </Button>
                    </div>
                    <div className="row gy-3 mt-3">

                        {/* Transportation Checkbox */}
                        <Form.Group className="col-md-4">
                            {/* <Form.Label>Transportation (INR)</Form.Label> */}
                            <Form.Check
                                type="checkbox"
                                label="Include Transportation ? (INR)"
                                name="transportation"
                                checked={orderData.transportation || false}
                                onChange={(e) =>
                                    setOrderData({
                                        ...orderData,
                                        transportation: e.target.checked,
                                        // reset amount if unchecked
                                        transportationAmount: e.target.checked ? orderData.transportationAmount : 0,
                                    })
                                }
                            />
                            <Form.Control
                                type="number"
                                name="transportationAmount"
                                value={orderData.transportationAmount || ""}
                                onChange={handleOrderChange}
                                disabled={!orderData.transportation}
                            />
                        </Form.Group>
                    </div>

                    {/* Lab Test */}
                    <div className="row mt-4">
                        <Form.Group className="mb-2">
                            <Form.Check
                                type="checkbox"
                                label="Lab Test Required"
                                name="labTestRequired"
                                checked={orderData.labTestRequired}
                                onChange={(e) =>
                                    setOrderData((prev) => ({ ...prev, labTestRequired: e.target.checked }))
                                }
                            />
                        </Form.Group>
                    </div>

                    {/* Totals */}
                    <div className="row mt-2 gy-3">
                        {/* Total Without Tax */}
                        <Form.Group className="col-md-4">
                            <Form.Label>Total Base Amount</Form.Label>
                            <Form.Control
                                readOnly
                                value={(() => {
                                    const subtotal = items.reduce((sum, it) => sum + (it.baseAmount || 0), 0);
                                    return subtotal.toFixed(2);
                                })()}
                            />
                        </Form.Group>

                        {/* Total Tax */}
                        <Form.Group className="col-md-4">
                            <Form.Label>Total Tax (INR)</Form.Label>
                            <Form.Control
                                readOnly
                                value={(() => {
                                    const totalTax = items.reduce((sum, it) => sum + (it.taxAmount || 0), 0);
                                    return totalTax.toFixed(2);
                                })()}
                            />
                        </Form.Group>

                        {/* Total With Tax */}
                        <Form.Group className="col-md-4">
                            <Form.Label>Total Amount (With Tax)</Form.Label>
                            <Form.Control
                                readOnly
                                value={(() => {
                                    const subtotal = items.reduce((sum, it) => sum + (it.baseAmount || 0), 0);
                                    const totalTax = items.reduce((sum, it) => sum + (it.taxAmount || 0), 0);
                                    const transportation = orderData.transportation ? (Number(orderData.transportationAmount) || 0) : 0;
                                    return (subtotal + totalTax + transportation).toFixed(2);
                                })()}
                            />
                        </Form.Group>
                    </div>
                    <div className="row mt-3 gy-3">
                        {/* EDD */}
                        <Form.Group className="col-md-4 mb-3">
                            <Form.Label>Expected Delivery Date (EDD)</Form.Label>
                            <Form.Control
                                type="date"
                                name="EDD"
                                value={orderData.EDD}
                                onChange={handleOrderChange}
                            />
                        </Form.Group>
                        {/* Order Date */}
                        <Form.Group className="col-md-4 mb-3">
                            <Form.Label>Order Date</Form.Label>
                            <Form.Control
                                type="date"
                                name="orderDate"
                                value={orderData.orderDate}
                                onChange={handleOrderChange}
                            />
                        </Form.Group>
                    </div>



                    <div className="row justify-content-between my-4">
                        <div className="col">
                            <Button onClick={() => setStep(1)}>Back</Button>
                        </div>
                        <div className="col text-end">
                            <Button variant="primary" onClick={handleUpdate}>
                                Update Sales Order
                            </Button>
                        </div>
                    </div>

                    <Modal show={showItemModal} onHide={handleCloseItemModal} size="lg" fullscreen="lg-down">
                        <Modal.Header closeButton>
                            <Modal.Title>{editingIndex !== null ? 'Edit Product Item' : 'Add Product Item'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form>
                                <Row className="gy-3">
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Product</Form.Label>
                                            <Form.Select name="productId" value={newItem.productId} onChange={handleItemChange}>
                                                <option value="">Select...</option>
                                                {products.map(p => (
                                                    <option key={p._id} value={p._id}>{p.productName}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Packing Bag Type</Form.Label>
                                            <Form.Select name="packingBagType" value={newItem.packingBagType} onChange={handleItemChange}>
                                                <option value="">Select...</option>
                                                {materials.map(m => (
                                                    <option key={m._id} value={m._id}>{m.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Packing Size</Form.Label>
                                            <Form.Control name="packingSize" value={newItem.packingSize} onChange={handleItemChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Quality</Form.Label>
                                            <Form.Control name="quality" value={newItem.quality} onChange={handleItemChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Quantity</Form.Label>
                                            <div style={{ position: "relative" }}>
                                                <Form.Control
                                                    type="number"
                                                    name="quantity"
                                                    value={newItem.quantity}
                                                    onChange={handleItemChange}
                                                />
                                                {/* Show unit as suffix */}
                                                {selectedProduct && selectedProduct.unitId.unitCode && (
                                                    <span
                                                        style={{
                                                            position: "absolute",
                                                            right: "10px",
                                                            top: "10px",
                                                            fontSize: "0.8em",
                                                            color: "#6c757d",
                                                        }}
                                                    >
                                                        {selectedProduct.unitId.unitCode}
                                                    </span>
                                                )}
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Unit Price (INR)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="unitPrice"
                                                value={newItem.unitPrice}
                                                onChange={handleItemChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Tax (%)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="tax"
                                                value={newItem.tax}
                                                onChange={handleItemChange}
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Tax Amount (INR)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="taxAmount"
                                                value={newItem.taxAmount ? Number(newItem.taxAmount).toFixed(2) : "0.00"}
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Total Price (INR)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="totalPrice"
                                                value={newItem.totalPrice ? Number(newItem.totalPrice).toFixed(2) : "0.00"}
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Gross/Net</Form.Label>
                                            <Form.Select name="grossOrNet" value={newItem.grossOrNet} onChange={handleItemChange}>
                                                <option value="Gross">Gross</option>
                                                <option value="Net">Net</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Custom Marking</Form.Label>
                                            <Form.Control
                                                name="customMarking"
                                                value={newItem.customMarking}
                                                onChange={handleItemChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseItemModal}>Cancel</Button>
                            <Button
                                variant="success"
                                onClick={() => {
                                    if (!newItem.productId || !newItem.quantity || !newItem.unitPrice) {
                                        toast.error("Please fill required fields");
                                        return;
                                    }
                                    
                                    if (editingIndex !== null) {
                                        // Update existing item
                                        const updatedItems = [...items];
                                        updatedItems[editingIndex] = { ...newItem };
                                        setItems(updatedItems);
                                        toast.success("Item updated in list");
                                    } else {
                                        // Add new item
                                        setItems(prev => [...prev, newItem]);
                                    }

                                    setNewItem({
                                        productId: "",
                                        quality: "",
                                        quantity: 0,
                                        unitPrice: 0,
                                        grossOrNet: "Gross",
                                        packingSize: "",
                                        packingBagType: "",
                                        EDD: "",
                                        labTestRequired: false,
                                        customMarking: "",
                                        taxAmount: 0,
                                        totalPrice: 0,
                                        tax: 0,
                                    });
                                    handleCloseItemModal();
                                }}
                            >
                                {editingIndex !== null ? 'Update Item' : 'Add Item'}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </>
            )
            }
        </Layout>
    );
}
